import { useEffect, useState, useSyncExternalStore } from "react";
import { createStore, del, get, set, values } from "idb-keyval";
import { api, errorMessage } from "@/lib/api";

const store = createStore("rollsmartr-offline", "pending-logs");
const listeners = new Set();
let snapshot = [];
let syncing = false;

const emit = () => listeners.forEach((fn) => fn());

async function refresh() {
  snapshot = (await values(store)).sort((a, b) => a.created_at.localeCompare(b.created_at));
  emit();
}
refresh().catch(() => {});

export const isNetworkError = (err) => !err?.response;

export async function savePending({ userId, text, date, timezone }) {
  const id = (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  await set(id, { id, user_id: userId, text, date, timezone, created_at: new Date().toISOString(), status: "pending", error: null }, store);
  await refresh();
  return id;
}

export async function discardPending(id) {
  await del(id, store);
  await refresh();
}

async function update(id, patch) {
  const cur = await get(id, store);
  if (cur) await set(id, { ...cur, ...patch }, store);
}

export async function syncPending(userId, onSynced) {
  if (syncing || !navigator.onLine) return;
  syncing = true;
  let synced = 0;
  try {
    for (const item of (await values(store)).filter((p) => p.user_id === userId)) {
      await update(item.id, { status: "syncing" });
      await refresh();
      try {
        await api.post("/sessions", { text: item.text, date: item.date, timezone: item.timezone, client_id: item.id });
        await del(item.id, store);
        synced += 1;
      } catch (err) {
        if (isNetworkError(err)) {
          await update(item.id, { status: "pending" });
          break;
        }
        const code = err.response.status;
        await update(item.id, { status: code === 429 ? "limit" : code >= 500 ? "pending" : "failed", error: errorMessage(err) });
        if (code === 429 || code >= 500) break;
      }
    }
  } finally {
    syncing = false;
    await refresh();
    if (synced && onSynced) onSynced(synced);
  }
}

export function usePending(userId) {
  const all = useSyncExternalStore((fn) => { listeners.add(fn); return () => listeners.delete(fn); }, () => snapshot);
  return all.filter((p) => p.user_id === userId);
}

export function useOnline() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  return online;
}
